/**
 * @fileoverview Google Analytics utilities with GDPR compliance
 * 
 * This module provides functions to initialize and manage Google Analytics
 * in compliance with GDPR requirements. Analytics is only loaded after
 * explicit user consent.
 * 
 * @author App'Ines Team
 * @version 1.0.0
 * @since 2025
 */

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_MEASUREMENT_ID = 'G-JNQPQQBJTD';

/**
 * Initializes Google Analytics after user consent
 * 
 * This function dynamically loads the Google Analytics script and initializes
 * tracking. It should only be called after the user has given explicit consent
 * for cookie usage.
 * 
 * @example
 * // Initialize GA after user consent
 * initializeGoogleAnalytics();
 */
export function initializeGoogleAnalytics(): void {
  try {
    // Vérifier si GA n'est pas déjà initialisé
    if (window.gtag) {
      console.log('Google Analytics already initialized');
      return;
    }

    // Initialiser dataLayer si nécessaire
    window.dataLayer = window.dataLayer || [];
    
    // Fonction gtag
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // Configurer la date
    window.gtag('js', new Date());

    // Créer et charger le script Google Analytics
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    
    script.onload = () => {
      // Configurer Google Analytics après le chargement du script
      window.gtag('config', GA_MEASUREMENT_ID, {
        // Configuration RGPD-friendly
        anonymize_ip: true, // Anonymiser les adresses IP
        allow_google_signals: false, // Désactiver les signaux Google par défaut
        allow_ad_personalization_signals: false, // Désactiver la personnalisation publicitaire
        cookie_expires: 63072000, // 2 ans en secondes
        cookie_update: true,
        cookie_flags: 'SameSite=None;Secure', // Flags de cookies sécurisés
      });

      console.log('Google Analytics initialized successfully');
      
      // Envoyer un événement de consentement
      trackEvent('consent', 'cookie_consent', 'accepted');
    };

    script.onerror = () => {
      console.error('Failed to load Google Analytics script');
    };

    // Ajouter le script au document
    document.head.appendChild(script);

  } catch (error) {
    console.error('Error initializing Google Analytics:', error);
  }
}

/**
 * Tracks a custom event in Google Analytics
 * 
 * @param {string} action - The action being tracked
 * @param {string} category - The category of the event
 * @param {string} label - Optional label for the event
 * @param {number} value - Optional numeric value
 * 
 * @example
 * // Track a button click
 * trackEvent('click', 'button', 'cta_signup');
 * 
 * @example
 * // Track a form submission
 * trackEvent('submit', 'form', 'contact_form');
 */
export function trackEvent(
  action: string, 
  category: string, 
  label?: string, 
  value?: number
): void {
  if (!window.gtag) {
    console.warn('Google Analytics not initialized. Event not tracked:', { action, category, label, value });
    return;
  }

  try {
    const eventParams: any = {
      event_category: category,
    };

    if (label) eventParams.event_label = label;
    if (value !== undefined) eventParams.value = value;

    window.gtag('event', action, eventParams);
    
    console.log('Event tracked:', { action, category, label, value });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
}

// Track which pages have already been tracked to prevent duplicates
const trackedPages = new Set<string>();

/**
 * Tracks page views in Google Analytics
 * 
 * @param {string} pageTitle - The title of the page
 * @param {string} pagePath - The path of the page
 * 
 * @example
 * // Track a page view
 * trackPageView('Home Page', '/');
 */
export function trackPageView(pageTitle: string, pagePath: string): void {
  // Create a unique key for this page view
  const pageKey = `${pageTitle}:${pagePath}`;
  
  // If this exact page view was already tracked, skip it
  if (trackedPages.has(pageKey)) {
    console.log('Page view already tracked, skipping:', { pageTitle, pagePath });
    return;
  }
  
  if (!window.gtag) {
    console.warn('Google Analytics not initialized. Page view not tracked:', { pageTitle, pagePath });
    return;
  }

  try {
    // Mark this page as tracked
    trackedPages.add(pageKey);
    
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_title: pageTitle,
      page_location: window.location.href,
      page_path: pagePath,
    });

    console.log('Page view tracked:', { pageTitle, pagePath });
  } catch (error) {
    console.error('Error tracking page view:', error);
    // Remove from tracked set if there was an error
    trackedPages.delete(pageKey);
  }
}

/**
 * Tracks user interactions with the salary simulator
 * 
 * @param {string} interaction - Type of interaction (grade_selected, prime_activated, etc.)
 * @param {any} details - Additional details about the interaction
 * 
 * @example
 * // Track grade selection
 * trackSimulatorInteraction('grade_selected', { grade: 1, echelon: 6 });
 * 
 * @example
 * // Track prime activation
 * trackSimulatorInteraction('prime_activated', { primeId: 'segur', amount: 238 });
 */
export function trackSimulatorInteraction(interaction: string, details?: any): void {
  trackEvent('simulator_interaction', 'engagement', interaction, details?.amount);
  
  // Envoyer des détails supplémentaires si fournis
  if (details && window.gtag) {
    window.gtag('event', 'custom_simulator_event', {
      event_category: 'simulator',
      event_label: interaction,
      custom_parameter: JSON.stringify(details)
    });
  }
}

/**
 * Disables Google Analytics tracking
 * 
 * This function can be called when the user withdraws consent
 * or chooses to opt-out of tracking.
 */
export function disableGoogleAnalytics(): void {
  try {
    // Désactiver le tracking GA
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    
    // Supprimer les cookies GA existants
    const cookiesToDelete = [
      '_ga',
      '_ga_' + GA_MEASUREMENT_ID.replace('G-', ''),
      '_gid',
      '_gat',
      '_gat_gtag_' + GA_MEASUREMENT_ID
    ];

    cookiesToDelete.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });

    console.log('Google Analytics disabled and cookies cleared');
  } catch (error) {
    console.error('Error disabling Google Analytics:', error);
  }
}

/**
 * Checks if Google Analytics is currently active
 * 
 * @returns {boolean} True if GA is loaded and active
 */
export function isGoogleAnalyticsActive(): boolean {
  return typeof window.gtag === 'function' && !window[`ga-disable-${GA_MEASUREMENT_ID}`];
}

/**
 * Gets the current Google Analytics client ID
 * 
 * @returns {Promise<string | null>} The client ID or null if not available
 */
export function getGoogleAnalyticsClientId(): Promise<string | null> {
  return new Promise((resolve) => {
    if (!window.gtag) {
      resolve(null);
      return;
    }

    try {
      window.gtag('get', GA_MEASUREMENT_ID, 'client_id', (clientId: string) => {
        resolve(clientId || null);
      });
    } catch (error) {
      console.error('Error getting GA client ID:', error);
      resolve(null);
    }
  });
}